/**
 * A/B Testing Framework
 * Enables testing different variations of the landing page
 */

import React from 'react';

interface ABTest {
  id: string;
  name: string;
  description: string;
  variations: ABVariation[];
  traffic: number; // Percentage of traffic to include in test
  startDate: string;
  endDate?: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  goals: ABGoal[];
}

interface ABVariation {
  id: string;
  name: string;
  description: string;
  traffic: number; // Percentage of test traffic for this variation
  component?: string;
  config?: Record<string, any>;
}

interface ABGoal {
  id: string;
  name: string;
  type: 'conversion' | 'engagement' | 'revenue';
  eventName: string;
  target?: number;
}

interface ABTestResult {
  testId: string;
  variationId: string;
  userId?: string;
  sessionId: string;
  assignment: string;
  timestamp: string;
}

class ABTestingService {
  private static instance: ABTestingService;
  private currentTests: Map<string, ABTest> = new Map();
  private userAssignments: Map<string, Map<string, string>> = new Map();

  private constructor() {
    this.loadCurrentTests();
  }

  public static getInstance(): ABTestingService {
    if (!ABTestingService.instance) {
      ABTestingService.instance = new ABTestingService();
    }
    return ABTestingService.instance;
  }

  private loadCurrentTests(): void {
    // Define our current landing page tests
    const landingPageTest: ABTest = {
      id: 'landing_page_v1',
      name: 'Landing Page Optimization Test',
      description: 'Test new conversion-optimized landing page vs original',
      variations: [
        {
          id: 'original',
          name: 'Original Homepage',
          description: 'Current homepage with basic layout',
          traffic: 50,
          component: 'Homepage'
        },
        {
          id: 'optimized',
          name: 'Conversion-Optimized Landing Page', 
          description: 'New landing page with live demo, social proof, and clear CTAs',
          traffic: 50,
          component: 'NewLandingPage'
        }
      ],
      traffic: 100, // Include all visitors
      startDate: new Date().toISOString(),
      status: 'running',
      goals: [
        {
          id: 'demo_completion',
          name: 'Demo Completion Rate',
          type: 'conversion',
          eventName: 'demo_complete',
          target: 15 // Target 15% demo completion rate
        },
        {
          id: 'trial_signup',
          name: 'Trial Signup Rate',
          type: 'conversion', 
          eventName: 'trial_started',
          target: 5 // Target 5% trial signup rate
        },
        {
          id: 'time_on_page',
          name: 'Time on Page',
          type: 'engagement',
          eventName: 'time_on_page',
          target: 120 // Target 2+ minutes on page
        }
      ]
    };

    this.currentTests.set(landingPageTest.id, landingPageTest);
  }

  public getTestVariation(testId: string, userId?: string, sessionId?: string): string | null {
    const test = this.currentTests.get(testId);
    if (!test || test.status !== 'running') {
      return null;
    }

    const identifier = userId || sessionId || 'anonymous';
    
    // Check if user already has an assignment
    if (this.userAssignments.has(testId)) {
      const assignment = this.userAssignments.get(testId)?.get(identifier);
      if (assignment) {
        return assignment;
      }
    }

    // Check if user should be included in test
    if (!this.shouldIncludeInTest(test, identifier)) {
      return null;
    }

    // Assign to variation
    const variation = this.assignToVariation(test, identifier);
    
    // Store assignment
    if (!this.userAssignments.has(testId)) {
      this.userAssignments.set(testId, new Map());
    }
    this.userAssignments.get(testId)?.set(identifier, variation);

    // Track assignment
    this.trackAssignment(testId, variation, userId, sessionId);

    return variation;
  }

  private shouldIncludeInTest(test: ABTest, identifier: string): boolean {
    // Hash the identifier to get consistent assignment
    const hash = this.hashString(identifier + test.id);
    const percentage = (hash % 100) + 1;
    return percentage <= test.traffic;
  }

  private assignToVariation(test: ABTest, identifier: string): string {
    // Use hash for consistent assignment
    const hash = this.hashString(identifier + test.id + 'variation');
    const percentage = (hash % 100) + 1;
    
    let cumulative = 0;
    for (const variation of test.variations) {
      cumulative += variation.traffic;
      if (percentage <= cumulative) {
        return variation.id;
      }
    }
    
    // Fallback to first variation
    return test.variations[0].id;
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  private async trackAssignment(
    testId: string, 
    variationId: string, 
    userId?: string, 
    sessionId?: string
  ): Promise<void> {
    try {
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'ab_test_assignment',
          category: 'ab_testing',
          action: 'assign',
          label: `${testId}:${variationId}`,
          userId,
          sessionId,
          page: window.location.pathname,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          properties: {
            testId,
            variationId,
            testName: this.currentTests.get(testId)?.name
          }
        })
      });
    } catch (error) {
      console.error('Failed to track A/B test assignment:', error);
    }
  }

  public getCurrentTests(): ABTest[] {
    return Array.from(this.currentTests.values());
  }

  public getTestById(testId: string): ABTest | undefined {
    return this.currentTests.get(testId);
  }
}

// React hooks for A/B testing
export const useABTest = (testId: string, userId?: string, sessionId?: string) => {
  const [variation, setVariation] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const abService = ABTestingService.getInstance();
    const assignedVariation = abService.getTestVariation(testId, userId, sessionId);
    setVariation(assignedVariation);
    setIsLoading(false);
  }, [testId, userId, sessionId]);

  return { variation, isLoading };
};

export const useABTestComponent = (testId: string, defaultComponent: React.ComponentType) => {
  const { variation, isLoading } = useABTest(testId);

  if (isLoading) {
    return { Component: defaultComponent, variation: null, isLoading: true };
  }

  // Map variations to components
  const componentMap: Record<string, React.ComponentType> = {
    'original': React.lazy(() => import('../pages/Homepage')),
    'optimized': React.lazy(() => import('../pages/NewLandingPage'))
  };

  const Component = variation && componentMap[variation] ? componentMap[variation] : defaultComponent;

  return { Component, variation, isLoading: false };
};

// Landing page A/B test hook
export const useLandingPageTest = () => {
  return useABTestComponent('landing_page_v1', React.lazy(() => import('../pages/Homepage')));
};

// Export service instance
export const abTestingService = ABTestingService.getInstance();
export default ABTestingService;